import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, markAuthenticated, markSignedOut, setActiveSupportSession, setInMemoryCsrf } from "../lib/api";

const AuthContext = createContext(null);
export { AuthContext };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [supportSession, setSupportSessionState] = useState(null);
  // Every auth operation owns a generation. A slower, older anonymous probe
  // must never overwrite a newer successful login (observed most often on
  // mobile Safari and slower networks).
  const authGeneration = useRef(0);

  const setSupportSession = useCallback((session) => {
    setActiveSupportSession(session?.id || "");
    setSupportSessionState(session || null);
  }, []);

  const refresh = useCallback(async () => {
    const generation = ++authGeneration.current;
    try {
      const csrfRes = await api.get("/auth/csrf");
      setInMemoryCsrf(csrfRes.data?.csrf_token || "");
      const { data } = await api.get("/auth/me");
      if (generation !== authGeneration.current) return;
      setUser(data);
      markAuthenticated();
    } catch (e) {
      if (generation !== authGeneration.current) return;
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const handler = () => {
      ++authGeneration.current;
      setUser(false);
      setSupportSession(null);
      markSignedOut();
    };
    window.addEventListener("rk:session-expired", handler);
    return () => window.removeEventListener("rk:session-expired", handler);
  }, []);

  const login = async (email, password, mfaCode, backupCode) => {
    // Invalidate any initial anonymous refresh before beginning login. Without
    // this guard, its delayed 401 can replace a valid mobile session with false.
    const generation = ++authGeneration.current;
    const csrfRes = await api.get("/auth/csrf");
    setInMemoryCsrf(csrfRes.data?.csrf_token || "");
    const { data } = await api.post("/auth/login", {
      email, password,
      ...(mfaCode ? { mfa_code: mfaCode } : {}),
      ...(backupCode ? { backup_code: backupCode } : {}),
    });
    if (data.mfa_required) return { mfaRequired: true };
    setInMemoryCsrf(data.csrf_token || "");
    if (generation !== authGeneration.current) return data;
    setUser(data);
    markAuthenticated();
    return data;
  };

  const logout = async () => {
    ++authGeneration.current;
    try {
      await api.post("/auth/logout");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail !== "csrf_check_failed") throw error;
      const csrfRes = await api.get("/auth/csrf");
      setInMemoryCsrf(csrfRes.data?.csrf_token || "");
      await api.post("/auth/logout");
    }
    setUser(false);
    setSupportSession(null);
    markSignedOut();
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, refresh, login, logout,
      supportSession, setSupportSession,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
